<template>
  <div class="profile-page">
    <!-- Impersonation Banner -->
    <div v-if="showImpersonationBanner"
      class="alert alert-warning alert-dismissible fade show mb-3 impersonation-banner" role="alert">
      <div class="d-flex align-items-center justify-content-between">
        <div class="d-flex align-items-center">
          <i class="bi bi-person-badge me-2"></i>
          <div>
            <strong>Impersonating:</strong> {{ impersonatedUser.name }} ({{ impersonatedUser.email }})
            <br>
            <small class="text-muted">You are viewing and can edit {{ impersonatedUser.name }}'s profile</small>
          </div>
        </div>
        <button type="button" class="btn btn-sm btn-outline-dark" @click="stopImpersonation"
          title="Return to your account">
          <i class="bi bi-arrow-left me-1"></i>Stop Impersonation
        </button>
      </div>
    </div>
    <!-- Loading State -->
    <div v-if="loading" class="d-flex justify-content-center align-items-center" style="height: 400px;">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <h4 class="alert-heading">Error Loading Profile</h4>
      <p>{{ error }}</p>
      <button class="btn btn-outline-danger" @click="loadProfile">
        <i class="bi bi-arrow-clockwise me-2"></i>Retry
      </button>
    </div>
    <!-- Profile Content -->
    <div v-else>
      <!-- Hero Section with Background -->
      <div class="profile-hero-section"
        style="background: #fff; border-radius: 10px; box-shadow: 0 2px 6px 0 rgba(67, 89, 113, 0.12);">
        <div class="profile-hero-background"></div>
        <div class="profile-hero-content">
          <div class="row align-items-end">
            <div class="col-auto">
              <div class="profile-picture-wrap" :class="{ 'is-editable': canEditProfileAvatar }">
                <img :src="userAvatar" alt="Profile Picture" class="profile-picture"
                  @error="onAvatarError($event, profileUser || user)">
                <button v-if="canEditProfileAvatar" type="button" class="avatar-edit-btn" :disabled="saving"
                  title="Change profile image" @click="$refs.profileAvatarInput.click()">
                  <i class="bi bi-pencil-fill"></i>
                </button>
                <input ref="profileAvatarInput" type="file" accept="image/*" class="d-none"
                  @change="onAvatarFileSelected">
              </div>
            </div>
            <div class="col">
              <h1 class="profile-name mb-1">{{ displayName }}</h1>
              <div class="profile-meta">
                <span class="meta-item" v-if="displayProfession">
                  <i class="bi bi-briefcase me-1"></i>
                  {{ displayProfession }}
                </span>
                <span class="meta-item" v-if="displayCountry">
                  <i class="bi bi-geo-alt me-1"></i>
                  {{ displayCountry }}
                </span>
                <span class="meta-item">
                  <i class="bi bi-clock me-1"></i>
                  {{ formatDate(user?.created_at) }}
                </span>
              </div>
            </div>
            <div class="col-auto">
              <button class="btn btn-primary" @click="showEditProfileModal" style="color: #fff" :disabled="saving">
                <i class="bi bi-pencil me-2"></i>Edit Profile
              </button>
            </div>
          </div>
        </div>
      </div>
      <!-- Main Content -->
      <div class="container-fluid mt-4" style="padding: 0">
        <div class="row">
          <!-- Left Sidebar -->
          <div class="col-md-4">
            <!-- Overview Card -->
            <div class="card mb-4">
              <div class="card-header">
                <h6 class="mb-0">OVERVIEW</h6>
              </div>
              <div class="card-body">
                <div class="overview-item">
                  <i class="bi bi-folder2 me-2"></i>
                  <span class="label">Projects:</span>
                  <span class="value">{{ profileOverview.projects }}</span>
                </div>
                <div class="overview-item">
                  <i class="bi bi-award me-2"></i>
                  <span class="label">Grants:</span>
                  <span class="value">{{ profileOverview.grants }}</span>
                </div>
                <div class="overview-item">
                  <i class="bi bi-people me-2"></i>
                  <span class="label">Memberships:</span>
                  <span class="value">{{ profileOverview.memberships }}</span>
                </div>
              </div>
            </div>
            <!-- Contact Information -->
            <div class="card mb-4">
              <div class="card-header">
                <h6 class="mb-0">PERSONAL DETAILS</h6>
              </div>
              <div class="card-body">
                <div class="row mb-3 align-items-start align-items-lg-center">
                  <div class="col-12 col-lg-4 d-flex align-items-center mb-2 mb-lg-0">
                    <i class="bi bi-envelope me-2"></i>
                    <strong>Email:</strong>
                  </div>
                  <div class="col-12 col-lg-8">
                    {{ displayEmail }}
                  </div>
                </div>
                <div class="row mb-3 align-items-start align-items-lg-center">
                  <div class="col-12 col-lg-4 d-flex align-items-center mb-2 mb-lg-0">
                    <i class="bi bi-building me-2"></i>
                    <strong>Organisation:</strong>
                  </div>
                  <div class="col-12 col-lg-8">
                    {{ displayOrganization }}
                  </div>
                </div>
                <div class="row mb-3 align-items-start align-items-lg-center">
                  <div class="col-12 col-lg-4 d-flex align-items-center mb-2 mb-lg-0">
                    <i class="bi bi-briefcase me-2"></i>
                    <strong>Job Title:</strong>
                  </div>
                  <div class="col-12 col-lg-8">
                    {{ displayJobTitle }}
                  </div>
                </div>
                <div class="row mb-3 align-items-start align-items-lg-center">
                  <div class="col-12 col-lg-4 d-flex align-items-center mb-2 mb-lg-0">
                    <i class="bi bi-geo-alt me-2"></i>
                    <strong>Country:</strong>
                  </div>
                  <div class="col-12 col-lg-8">
                    {{ displayCountry }}
                  </div>
                </div>
              </div>
            </div>
            <!-- Working Group 1 -->
            <div class="card mb-4" v-if="displayWorkingGroup1">
              <div class="card-header">
                <h6 class="mb-0">WORKING GROUP 1</h6>
              </div>
              <div class="card-body">
                <div class="working-group-item">
                  <i class="bi bi-people-fill me-1"></i>{{ displayWorkingGroup1 }}
                </div>
              </div>
            </div>

            <!-- Working Group 2 -->
            <div class="card mb-4" v-if="displayWorkingGroup2">
              <div class="card-header">
                <h6 class="mb-0">WORKING GROUP 2</h6>
              </div>
              <div class="card-body">
                <div class="working-group-item">
                  <i class="bi bi-people-fill me-1"></i>{{ displayWorkingGroup2 }}
                </div>
              </div>
            </div>
            <!-- Related Fellows -->
            <div class="card">
              <div class="card-header">
                <h6 class="mb-0">RELATED FELLOWS</h6>
              </div>
              <div class="card-body">
                <!-- If there are fellows -->
                <div v-if="relatedFellows && relatedFellows.length > 0" v-for="fellow in relatedFellows"
                  :key="fellow.id" class="fellow-item d-flex align-items-center mb-3">
                  <img :src="getUserAvatar(fellow)" alt="" class="fellow-avatar me-3 rounded-circle shadow-sm"
                    width="45" height="45" @error="onAvatarError($event, fellow)" />
                  <div class="flex-grow-1">
                    <div class="fellow-name fw-semibold">{{ fellow.name }}</div>
                    <div class="fellow-title text-muted small">{{ fellow.title }}</div>
                  </div>
                  <!--                  <button class="btn btn-sm btn-outline-primary">View</button>-->
                </div>

                <!-- If there are no fellows -->
                <div v-else class="text-center py-4 text-muted">
                  <i class="bi bi-people-fill fs-3 d-block mb-2 text-secondary"></i>
                  <p class="mb-0">No related fellows to display</p>
                  <small v-if="userHasNoWorkingGroup" class="text-muted">
                    Join a working group to connect with other fellows.
                  </small>
                </div>
              </div>
            </div>
          </div>
          <!-- Right Content Area -->
          <div class="col-md-8">
            <!-- Activity Timeline Component -->
            <ActivityTimeline />
            <!-- Expandable Sections -->
            <div class="expandable-sections">
              <!-- Education -->
              <div class="card mb-3" v-if="displayEducation.length">
                <div class="card-header" @click="toggleSection('education')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Education</h6>
                    <i class="bi" :class="sections.education ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.education" class="card-body">
                  <div v-for="edu in displayEducation" :key="edu.id || edu.institution" class="mb-4 pb-3 border-bottom">
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-2">
                          <small class="text-muted">Institution</small>
                          <p class="mb-0">{{ edu.institution || 'N/A' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-2">
                          <small class="text-muted">Degree</small>
                          <p class="mb-0">{{ edu.degree || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                    <div class="row">
                      <div class="col-md-6">
                        <div class="mb-2">
                          <small class="text-muted">Country</small>
                          <p class="mb-0">{{ getCountryName(edu.country) || 'N/A' }}</p>
                        </div>
                      </div>
                      <div class="col-md-6">
                        <div class="mb-2">
                          <small class="text-muted">Year</small>
                          <p class="mb-0">{{ edu.year || 'N/A' }}</p>
                        </div>
                      </div>
                    </div>
                    <div v-if="edu.fos" class="row">
                      <div class="col-md-12">
                        <div class="mb-2">
                          <small class="text-muted">Field of Study</small>
                          <p class="mb-0">{{ edu.fos }}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Professional Positions -->
              <div class="card mb-3" v-if="displayProfPositions.length">
                <div class="card-header" @click="toggleSection('positions')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Professional Experience</h6>
                    <i class="bi" :class="sections.positions ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.positions" class="card-body">
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                        <tr class="text-primary">
                          <th>Dates</th>
                          <th>Position</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="pos in displayProfPositions" :key="pos.id || pos.position">
                          <td>{{ pos.dates || 'N/A' }}</td>
                          <td>{{ pos.position || 'N/A' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- Other Experience -->
              <div class="card mb-3" v-if="displayExperience.length">
                <div class="card-header" @click="toggleSection('experience')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Professional Activities</h6>
                    <i class="bi" :class="sections.experience ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.experience" class="card-body">
                  <div class="table-responsive">
                    <table class="table table-borderless">
                      <thead>
                        <tr class="text-primary">
                          <th>Dates</th>
                          <th>Role</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="exp in displayExperience" :key="exp.id || exp.position">
                          <td>{{ exp.dates || 'N/A' }}</td>
                          <td>{{ exp.position || 'N/A' }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
              <!-- Grants -->
              <div class="card mb-3" v-if="displayGrants.length">
                <div class="card-header" @click="toggleSection('grants')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Grants ({{ displayGrants.length }})</h6>
                    <i class="bi" :class="sections.grants ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.grants" class="card-body">
                  <div v-for="(grant, index) in displayGrants" :key="grant.id || index"
                    class="mb-4 p-3 border rounded bg-light">
                    <div class="row">
                      <div class="col-md-4">
                        <table class="table table-borderless table-sm">
                          <tbody>
                            <tr>
                              <td><strong class="text-primary">Title:</strong></td>
                              <td>{{ grant.title || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td><strong class="text-primary">Code:</strong></td>
                              <td>{{ grant.code || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td><strong class="text-primary">Role:</strong></td>
                              <td>{{ grant.role || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td><strong class="text-primary">Amount:</strong></td>
                              <td>{{ formatCurrency(grant.amount, grant.currency) }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                      <div class="col-md-6">
                        <table class="table table-borderless table-sm">
                          <tbody>
                            <tr>
                              <td><strong class="text-primary">Funding Source:</strong></td>
                              <td>{{ grant.fundingSource || 'N/A' }}</td>
                            </tr>
                            <tr>
                              <td><strong class="text-primary">Period:</strong></td>
                              <td>{{ formatGrantPeriod(grant) }}</td>
                            </tr>
                            <tr>
                              <td><strong class="text-primary">EDCTP Fellowship:</strong></td>
                              <td>{{ grant.isEdctpFellowship ? 'Yes' : 'No' }}</td>
                            </tr>
                            <tr v-if="grant.fellowshipToLink">
                              <td><strong class="text-primary">Linked Fellowship:</strong></td>
                              <td>{{ grant.fellowshipToLink }}</td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <!-- Publications -->
              <div class="card mb-3" v-if="displayPublications.length">
                <div class="card-header" @click="toggleSection('publications')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Publications ({{ displayPublications.length }})</h6>
                    <i class="bi" :class="sections.publications ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.publications" class="card-body">
                  <div v-for="(publication, index) in visiblePublications" :key="publication.id || index"
                    class="mb-3 p-3 border rounded">
                    <h6>{{ publication.title || `Publication ${index + 1}` }}</h6>
                    <dl class="publication-meta">
                      <template v-if="publication.journal">
                        <dt class="text-primary">Journal:</dt>
                        <dd>{{ publication.journal }}</dd>
                      </template>
                      <template v-if="publication.date">
                        <dt class="text-primary">Date:</dt>
                        <dd>{{ formatDate(publication.date) }}</dd>
                      </template>
                      <template v-if="publication.keywords">
                        <dt class="text-primary">Keywords:</dt>
                        <dd>{{ publication.keywords }}</dd>
                      </template>
                      <template v-if="publication.authors && publication.authors.length">
                        <dt class="text-primary">Authors:</dt>
                        <dd>{{ sortedAuthorNames(publication) }}</dd>
                      </template>
                    </dl>
                  </div>
                </div>
              </div>
              <!-- Projects -->
              <div class="card" v-if="displayProjects.length">
                <div class="card-header" @click="toggleSection('projects')" style="cursor: pointer;">
                  <div class="d-flex justify-content-between align-items-center">
                    <h6 class="mb-0">Projects ({{ displayProjects.length }})</h6>
                    <i class="bi" :class="sections.projects ? 'bi-chevron-up' : 'bi-chevron-down'"></i>
                  </div>
                </div>
                <div v-if="sections.projects" class="card-body">
                  <div v-if="displayProjects.length" class="mt-3">
                    <div v-for="(project, index) in displayProjects" :key="index" class="mb-3 p-3 border rounded">
                      <h6>{{ project.title || `Project ${index + 1}` }}</h6>
                      <div class="row">
                        <div class="col-md-6">
                          <table class="table table-borderless table-sm">
                            <tbody>
                              <tr v-if="project.startDate || project.endDate">
                                <td><strong class="text-primary">Duration:</strong></td>
                                <td>{{ project.startDate }} - {{ project.endDate }}</td>
                              </tr>
                              <tr v-if="project.year">
                                <td><strong class="text-primary">Year:</strong></td>
                                <td>{{ project.year }}</td>
                              </tr>
                              <tr v-if="project.type">
                                <td><strong class="text-primary">Type:</strong></td>
                                <td>{{ project.type }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                        <div class="col-md-6">
                          <table class="table table-borderless table-sm">
                            <tbody>
                              <tr v-if="project.code">
                                <td><strong class="text-primary">Code:</strong></td>
                                <td>{{ project.code }}</td>
                              </tr>
                              <tr v-if="project.grant">
                                <td><strong class="text-primary">Grant:</strong></td>
                                <td>{{ project.grant }}</td>
                              </tr>
                            </tbody>
                          </table>
                        </div>
                      </div>
                      <div v-if="project.objectives" class="mt-2">
                        <strong class="text-primary">Objectives:</strong>
                        <p class="mt-1 small">{{ project.objectives }}</p>
                      </div>
                    </div>
                  </div>
                  <div v-else class="text-muted text-center py-3">
                    No projects available
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <!-- Edit Profile Modal -->
    <EditProfileModal :show="showEditModal" @close="closeEditProfileModal" @save="handleSaveProfile"
      @error="showErrorMessage" @reload-profile="reloadProfileData(true)" :profile-data="transformedProfileData" />
    <ProfileImageCropper v-model="showCropper" :image-src="cropperImageSrc" @cropped="onImageCropped"
      @cancel="cropperImageSrc = null" />
    <!-- Success/Error Messages -->
    <div v-if="successMessage" class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header bg-success text-white">
          <i class="bi bi-check-circle me-2"></i>
          <strong class="me-auto">Success</strong>
          <button type="button" class="btn-close btn-close-white" @click="successMessage = null"></button>
        </div>
        <div class="toast-body">
          {{ successMessage }}
        </div>
      </div>
    </div>
    <div v-if="errorMessage" class="toast-container position-fixed bottom-0 end-0 p-3">
      <div class="toast show" role="alert" aria-live="assertive" aria-atomic="true">
        <div class="toast-header bg-danger text-white">
          <i class="bi bi-exclamation-triangle me-2"></i>
          <strong class="me-auto">Error</strong>
          <button type="button" class="btn-close btn-close-white" @click="errorMessage = null"></button>
        </div>
        <div class="toast-body">
          {{ errorMessage }}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EditProfileModal from './EditProfileModal.vue'
import ActivityTimeline from '@/components/common/ActivityTimeline.vue'
import { mapGetters } from "vuex"
import ProfileApiService from "@/services/profile/ProfileApiService"
import { avatarMixin } from '@/mixins/avatarMixin'
import ApiService from '@/services/api';
import UserService from '@/services/users/UserService'
import AuthenticationService from '@/services/authentication/AuthenticationService'
import ProfileImageCropper from '@/components/common/ProfileImageCropper.vue'
import AnalyticsService from '@/services/analytics/AnalyticsService'
import { countryCodeToName, getCountryName } from '@/utils/countryMap'

export default {
  name: 'MyProfile',
  mixins: [avatarMixin],
  components: {
    EditProfileModal,
    ProfileImageCropper,
    ActivityTimeline
  },
  data() {
    return {
      userHasNoWorkingGroup: false,
      showEditModal: false,
      profileData: {},
      loading: true,
      saving: false,
      error: null,
      successMessage: null,
      errorMessage: null,
      sections: {
        education: false,
        positions: false,
        experience: false,
        grants: false,
        publications: false,
        projects: false
      },
      relatedFellows: [],
      workingGroupsMapping: [],
      showCropper: false,
      cropperImageSrc: null,
      avatarRefreshVersion: null,
      isImpersonating: false,
      impersonatedUser: null,
      originalUser: null,
      impersonationBanner: null
    }
  },
  computed: {
    ...mapGetters('auth', ['user']),

    countryMap() {
      return countryCodeToName
    },

    // Exposed for the template: a module-level import is not on the component
    // instance, so calling getCountryName() directly in the markup threw
    // "getCountryName is not a function".
    getCountryName() {
      return getCountryName
    },

    transformedProfileData() {
      return this.transformDataForModal(this.profileData)
    },

    canEditProfileAvatar() {
      return Boolean(this.profileUser?.id || this.user?.id)
    },

    userAvatar() {
      const avatarUrl = this.getUserAvatar(this.profileUser || this.user)
      return this.withCacheBuster(avatarUrl, this.avatarRefreshVersion)
    },

    profileUser() {
      return this.profileData?.user || {}
    },

    fellowDetailsData() {
      return this.profileData?.user?.fellow_details || {}
    },

    profileInfo() {
      return this.profileData?.personal || {}
    },

    displayName() {
      const name = this.profileUser?.name || 'User'  // Use profileUser, not this.user
      return this.isImpersonating ? `${name} (Impersonating)` : name
    },

    showImpersonationBanner() {
      return this.isImpersonating && this.originalUser && this.impersonatedUser
    },

    effectiveUser() {
      return this.isImpersonating ? this.impersonatedUser : this.user
    },

    displayProfession() {
      return this.fellowDetailsData?.current_job_title || ''
    },

    displayCountry() {
      const code =
        this.profileInfo?.country ||
        this.fellowDetailsData?.country ||
        ''

      if (!code) return ''

      // If already a full name (longer than 2 chars), return as-is
      if (code.length > 2) return code

      // Convert country code to name (handles both African and international countries)
      return getCountryName(code) || code
    },

    displayEmail() {
      return this.profileUser?.email || ''
    },
    displayOrganization() {
      return this.fellowDetailsData?.current_organisation || ''
    },

    displayJobTitle() {
      return this.fellowDetailsData?.current_job_title || ''
    },

    displayWorkingGroup1() {
      const workingGroupsData = this.getWorkingGroupsData()
      if (Array.isArray(workingGroupsData) && workingGroupsData.length > 0) {
        return this.getWorkingGroupDisplayName(workingGroupsData[0])
      }
      return null
    },

    displayWorkingGroup2() {
      const workingGroupsData = this.getWorkingGroupsData()
      if (Array.isArray(workingGroupsData) && workingGroupsData.length > 1) {
        return this.getWorkingGroupDisplayName(workingGroupsData[1])
      }
      return null
    },

    displayEducation() {
      const education = this.fellowDetailsData?.education
      return Array.isArray(education) ? education : []
    },

    displayProfPositions() {
      const positions = this.fellowDetailsData?.prof_pos
      if (Array.isArray(positions)) {
        return positions.map(pos => ({
          ...pos,
          dates: this.formatDateRange(pos.startDate || pos.start_date, pos.endDate || pos.end_date)
        }))
      }
      return []
    },

    displayExperience() {
      const experience = this.fellowDetailsData?.experience
      if (Array.isArray(experience)) {
        return experience.map(exp => ({
          ...exp,
          dates: this.formatDateRange(exp.startDate || exp.start_date, exp.endDate || exp.end_date)
        }))
      }
      return []
    },

    displayGrants() {
      const grants = this.profileData?.grants
      return Array.isArray(grants) ? grants : []
    },

    displayPublications() {
      const publications = this.fellowDetailsData?.publications
      return Array.isArray(publications) ? publications : []
    },

    // Only the first 2 are rendered; the header still shows the full count
    // via displayPublications.length.
    visiblePublications() {
      return this.displayPublications.slice(0, 2)
    },

    displayProjects() {
      const projects = this.fellowDetailsData?.current_projects
      try {
        if (typeof projects === 'string') {
          const parsed = JSON.parse(projects)
          return Array.isArray(parsed) ? parsed : []
        } else if (Array.isArray(projects)) {
          return projects
        }
      } catch (e) {
        console.warn('Error parsing projects:', e)
      }
      return []
    },

    displayMemberships() {
      const memberships = this.fellowDetailsData?.memberships
      return Array.isArray(memberships) ? memberships : []
    },

    profileOverview() {
      return {
        projects: this.displayProjects.length,
        grants: this.displayGrants.length,
        memberships: this.displayMemberships.length
      }
    }
  },

  async mounted() {
    // Check impersonation state first
    await this.checkImpersonationState()

    await this.loadWorkingGroupsMapping()
    await this.loadProfile()
    await this.loadRelatedFellows()
  },

  methods: {
    clearImpersonationState() {
      this.isImpersonating = false
      this.impersonatedUser = null
      this.originalUser = null
      this.currentUser = null

      // Clear from localStorage
      localStorage.removeItem('impersonation_active')
      localStorage.removeItem('impersonation_data')

      // Clear from Vuex if applicable
      if (this.$store && this.$store.state.auth) {
        this.$store.commit('auth/SET_IMPERSONATION', {
          isImpersonating: false,
          originalUser: null,
          impersonatedUser: null
        })
      }
    },

    async reloadProfileData(forceFresh = false) {
      try {
        await this.loadProfile(forceFresh)
      } catch (error) {
        console.error('Error reloading profile data:', error)
      }
    },
    async checkImpersonationState() {
      try {
        const token = ApiService.getAuthToken()
        if (!token) {
          this.clearImpersonationState()
          return
        }

        const response = await UserService.getImpersonationStatus()

        let data = null
        if (response?.Data?.status === 'success') {
          data = response.Data.data
        } else if (response?.status === 'success') {
          data = response.data
        }

        if (data && data.is_impersonating) {
          this.isImpersonating = true
          this.originalUser = data.original_user
          this.impersonatedUser = data.impersonated_user
          this.currentUser = data.impersonated_user

          // Update Vuex
          if (this.$store && data.impersonated_user) {
            this.$store.commit('auth/SET_USER', data.impersonated_user)
            this.$store.commit('auth/SET_IMPERSONATION', {
              isImpersonating: true,
              originalUser: data.original_user,
              impersonatedUser: data.impersonated_user
            })
          }

          localStorage.setItem('impersonation_active', 'true')
          localStorage.setItem('impersonation_data', JSON.stringify({
            original_user: data.original_user,
            impersonated_user: data.impersonated_user
          }))
        } else {
          this.clearImpersonationState()
        }
      } catch (error) {
        console.error('Error checking impersonation state:', error)
        this.clearImpersonationState()
      }
    },

    // Working Groups Methods
    async loadWorkingGroupsMapping() {
      try {
        const ForumService = await import('@/services/forums/ForumServices')
        const response = await ForumService.default.getWorkingGroups()

        if (response?.success && Array.isArray(response.data)) {
          this.workingGroupsMapping = response.data
        } else if (response?.data?.data && Array.isArray(response.data.data)) {
          // ✅ The real list might be nested here
          this.workingGroupsMapping = response.data.data
        } else if (response?.data && Array.isArray(response.data)) {
          this.workingGroupsMapping = response.data
        } else {
          this.workingGroupsMapping = []
        }

      } catch (error) {
        console.error('Profile - Error loading working groups mapping:', error)
        this.workingGroupsMapping = []
      }
    },

    getWorkingGroupsData() {
      // Check personal data first (from the form)
      if (this.profileInfo?.workingGroups && Array.isArray(this.profileInfo.workingGroups)) {
        return this.profileInfo.workingGroups
      }
      // Check fellow details working_group (from the API)
      else if (this.fellowDetailsData?.working_group) {
        const wgString = this.fellowDetailsData.working_group
        if (typeof wgString === 'string' && wgString.trim()) {
          return wgString.split(',').map(g => g.trim()).filter(g => g)
        }
      }
      return []
    },

    getWorkingGroupDisplayName(workingGroupValue) {
      if (!workingGroupValue) return null

      const groups = Array.isArray(this.workingGroupsMapping) ? this.workingGroupsMapping : []
      if (!groups.length) {
        console.warn('⚠️ Working groups not yet loaded.')
        return 'Working Group'
      }

      const group = groups.find(g => String(g.id).trim() === String(workingGroupValue).trim())

      if (group) return group.name

      return 'Working Group'
    },

    // Avatar and Activity Methods
    onAvatarFileSelected(event) {
      const file = event.target.files?.[0]
      if (!file) return

      // Reset input so the same file can be selected again
      event.target.value = ''

      const reader = new FileReader()
      reader.onload = (e) => {
        this.cropperImageSrc = e.target.result
        this.showCropper = true
      }
      reader.readAsDataURL(file)
    },

    async onImageCropped(base64DataUrl) {
      const userId = this.profileUser?.id || this.user?.id
      if (!userId) {
        this.showErrorMessage('Unable to determine profile owner for image upload.')
        return
      }

      await this.uploadCroppedAvatar(userId, base64DataUrl)
    },

    async uploadCroppedAvatar(userId, base64DataUrl) {
      try {
        this.saving = true

        const response = await UserService.uploadAvatarBase64(userId, base64DataUrl)
        const isSuccess = response?.Data?.status === 'success' || response?.status === 'success'

        if (!isSuccess) {
          throw new Error('Profile image upload failed')
        }

        AnalyticsService.trackEvent('avatar_upload')

        const avatarUrl = response?.Data?.data?.avatar_url || response?.data?.avatar_url
        if (avatarUrl) {
          const avatarVersion = Date.now()
          const cacheBustedAvatarUrl = this.withCacheBuster(avatarUrl, avatarVersion)
          this.avatarRefreshVersion = avatarVersion

          if (!this.profileData.user) this.profileData.user = {}
          this.profileData.user.avatar = cacheBustedAvatarUrl
          this.profileData.user.avatar_url = cacheBustedAvatarUrl
          this.profileData.user.avatar_version = avatarVersion

          if (String(this.$store?.state?.auth?.user?.id) === String(userId)) {
            this.$store.commit('auth/SET_USER', {
              ...this.$store.state.auth.user,
              avatar: cacheBustedAvatarUrl,
              avatar_url: cacheBustedAvatarUrl,
              avatar_version: avatarVersion
            })
          }

          if (String(this.$store?.state?.users?.currentUser?.id) === String(userId)) {
            this.$store.commit('users/SET_CURRENT_USER', {
              ...this.$store.state.users.currentUser,
              avatar: cacheBustedAvatarUrl,
              avatar_url: cacheBustedAvatarUrl,
              avatar_version: avatarVersion
            })
          }

          if (
            this.$store?.state?.users?.isImpersonating &&
            String(this.$store?.state?.users?.impersonatedUser?.id) === String(userId)
          ) {
            this.$store.commit('users/SET_IMPERSONATION', {
              isImpersonating: true,
              originalUser: this.$store.state.users.originalUser,
              impersonatedUser: {
                ...this.$store.state.users.impersonatedUser,
                avatar: cacheBustedAvatarUrl,
                avatar_url: cacheBustedAvatarUrl,
                avatar_version: avatarVersion
              }
            })
          }

          window.dispatchEvent(
            new CustomEvent('user-avatar-updated', {
              detail: {
                userId: String(userId),
                avatar: cacheBustedAvatarUrl,
                avatarVersion
              }
            })
          )

          // Keep UI responsive with optimistic update, then reconcile fresh data.
          setTimeout(() => {
            this.refreshAllData()
          }, 600)
        }

        this.showSuccessMessage('Profile image updated successfully!')
      } catch (error) {
        console.error('Error uploading cropped avatar from My Profile:', error)
        const errorMessage =
          error?.response?.data?.Data?.message ||
          error?.response?.data?.message ||
          error?.message ||
          'Failed to upload profile image'
        this.showErrorMessage(errorMessage)
      } finally {
        this.saving = false
        this.cropperImageSrc = null
      }
    },


    // Data Loading Methods
    async loadProfile(forceFresh = false) {
      try {
        this.loading = true
        this.error = null

        // Get the current user's profile (which will be the impersonated user if impersonating)
        const response = await ProfileApiService.getProfile(forceFresh)

        if (response) {
          let actualProfileData = response
          if (response.success && response.data) {
            actualProfileData = response.data
          }

          this.profileData = actualProfileData

        } else {
          this.profileData = {}
        }
      } catch (error) {
        console.error('Error loading profile:', error)
        this.error = error.message || 'Failed to load profile data'
        this.profileData = {}

        // If it's an authentication error and we're impersonating, something went wrong
        if (error?.response?.status === 401 && this.isImpersonating) {
          this.error = 'Impersonation session may have expired. Please try refreshing the page.'
        }
      } finally {
        this.loading = false
      }
    },

    async loadRelatedFellows() {
      try {
        const response = await ProfileApiService.get('/my-profile/related-fellows')

        let fellowsData = []

        // 🧠 Handle your exact shape (Success + Data + data[])
        if (response?.Data?.success && Array.isArray(response.Data.data)) {
          fellowsData = response.Data.data
        }
        // 🧠 Common Laravel/Axios format
        else if (response?.data?.success && Array.isArray(response.data.data)) {
          fellowsData = response.data.data
        }
        // 🧠 Simplified forms
        else if (response?.success && Array.isArray(response.data)) {
          fellowsData = response.data
        } else if (Array.isArray(response?.Data)) {
          fellowsData = response.Data
        } else if (Array.isArray(response)) {
          fellowsData = response
        }

        this.relatedFellows = fellowsData
        this.userHasNoWorkingGroup = fellowsData.length === 0

      } catch (error) {
        console.error('❌ Error loading related fellows:', error)
        this.relatedFellows = []
        this.userHasNoWorkingGroup = true
      }
    },

    // Activity timeline now lives in its own <ActivityTimeline /> component
    // (loads its own data), which superseded this inline implementation.

    // Profile Management Methods
    async handleSaveProfile(updatedData) {
      try {
        this.saving = true
        this.errorMessage = null

        console.log('DEBUG - Profile save payload (publications):', updatedData.publications);


        // Validate the data
        const validation = ProfileApiService.validateProfileData(updatedData)
        if (!validation.isValid) {
          this.showErrorMessage(validation.errors.join(', '))
          return
        }

        // Add timestamp for tracking
        if (updatedData.personal) {
          updatedData.personal._updateTimestamp = Date.now()
        }

        // Save the profile (the API will automatically use the current authenticated user context)
        const response = await ProfileApiService.updateProfile(updatedData)

        if (response) {
          this.showSuccessMessage(
            this.isImpersonating
              ? `Profile updated successfully for ${this.impersonatedUser?.name}!`
              : 'Profile updated successfully!'
          )
          this.showEditModal = false

          // Reload the profile data
          await this.loadProfile()
        } else {
          throw new Error('Failed to update profile')
        }
      } catch (error) {
        console.error('Error saving profile:', error)

        let errorMessage = error.message || 'Failed to update profile'

        // Handle specific impersonation-related errors
        if (error?.response?.status === 403) {
          errorMessage = 'You do not have permission to edit this profile.'
        } else if (error?.response?.status === 401 && this.isImpersonating) {
          errorMessage = 'Impersonation session expired. Please refresh and try again.'
        }

        this.showErrorMessage(errorMessage)
      } finally {
        this.saving = false
      }
    },

    async refreshAllData() {
      try {
        await this.checkImpersonationState()
        await this.loadProfile()
        await this.loadRelatedFellows()

      } catch (error) {
        console.error('Error refreshing data:', error)
      }
    },

    async stopImpersonation() {
      try {
        const UserService = await import('@/services/users/UserService')
        const response = await UserService.default.stopImpersonation()

        if (response?.Data?.status === 'success' || response?.status === 'success') {
          // Clear impersonation state
          this.isImpersonating = false
          this.impersonatedUser = null
          this.originalUser = null

          const restoredToken = response?.Data?.data?.access_token || response?.data?.access_token

          if (!restoredToken) {
            throw new Error('No access token received for original user')
          }

          AuthenticationService.setToken(restoredToken)

          // Update Vuex state back to original user
          const originalUserData = response?.Data?.data?.original_user || response?.data?.original_user
          if (originalUserData && this.$store.state.auth) {
            AuthenticationService.setUserData(originalUserData)
            this.$store.commit('auth/SET_USER', originalUserData)
            this.$store.commit('auth/SET_TOKEN', AuthenticationService.getJwt())
            this.$store.commit('auth/SET_IMPERSONATION', {
              isImpersonating: false,
              originalUser: null,
              impersonatedUser: null
            })
          }

          this.showSuccessMessage(`Impersonation stopped. Welcome back, ${originalUserData?.name}!`)

          // Redirect to own dashboard without a full page reload
          setTimeout(() => {
            this.$router.push('/my-profile')
          }, 1500)
        }
      } catch (error) {
        console.error('Error stopping impersonation:', error)
        this.showErrorMessage('Failed to stop impersonation. Please try refreshing the page.')
      }
    },

    // Utility Methods
    toggleSection(section) {
      this.sections[section] = !this.sections[section]
    },

    formatDate(dateString) {
      return ProfileApiService.formatDate(dateString)
    },

    sortedAuthorNames(publication) {
      const names = (publication.authors || [])
        .map(author => (author?.name || '').trim())
        .filter(Boolean)

      return [...names].sort((a, b) => a.localeCompare(b)).join(', ')
    },

    formatDateRange(startDate, endDate) {
      const start = startDate ? new Date(startDate).getFullYear() : ''
      const end = endDate ? new Date(endDate).getFullYear() : 'Present'

      if (start && end && end !== 'Present') {
        return `${start} - ${end}`
      } else if (start && end === 'Present') {
        return `${start} - Present`
      } else if (end && end !== 'Present') {
        return `Until ${end}`
      }
      return 'N/A'
    },

    formatCurrency(amount, currency) {
      return ProfileApiService.formatCurrency(amount, currency)
    },

    showSuccessMessage(message) {
      this.successMessage = message
      setTimeout(() => {
        this.successMessage = null
      }, 5000)
    },

    showErrorMessage(message) {
      this.errorMessage = message
      setTimeout(() => {
        this.errorMessage = null
      }, 8000)
    },

    transformDataForModal(profileData) {
      if (!profileData) {
        return null
      }
      return {
        success: true,
        data: profileData
      }
    },

    async showEditProfileModal() {
      await this.reloadProfileData(true)
      this.showEditModal = true
    },

    closeEditProfileModal() {
      this.showEditModal = false
      this.reloadProfileData(true)
    },

    formatGrantPeriod(grant) {
      const start = grant?.startYear || grant?.start_year
      const end = grant?.endYear || grant?.end_year

      if (start && end) {
        return `${start} - ${end}`
      } else if (start) {
        return `${start} -`
      } else if (end) {
        return `- ${end}`
      } else {
        return 'N/A'
      }
    }
  },

  watch: {
    user: {
      handler(newUser, oldUser) {
        // If the user changes and we have a new valid user, reload profile
        if (newUser && newUser.id && newUser.id !== oldUser?.id) {
          this.loadProfile()
        }
      },
      immediate: false // Don't run immediately since we handle this in mounted()
    },

    // Watch for impersonation state changes
    isImpersonating: {
      handler(newState, oldState) {
        if (newState !== oldState) {
          console.log('Impersonation state changed:', { newState, oldState })
        }
      }
    }
  },

  beforeUnmount() {
    this.successMessage = null
    this.errorMessage = null
  }
}
</script>

<style scoped>
/* Add these styles to your existing <style> section */

.impersonation-banner {
  border-left: 4px solid #ffc107;
  background-color: #fff3cd;
  border-color: #ffeaa7;
  position: sticky;
  top: 0;
  z-index: 1000;
  margin-bottom: 1rem;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.impersonation-banner .alert-heading {
  color: #856404;
}

.impersonation-banner i.bi-person-badge {
  font-size: var(--font-size-lg);
  color: #856404;
}

.impersonation-banner .btn-outline-dark {
  border-color: #856404;
  color: #856404;
  background-color: transparent;
}

.profile-picture-wrap {
  position: relative;
  display: inline-block;
}

.profile-picture-wrap.is-editable:hover .avatar-edit-btn,
.profile-picture-wrap.is-editable:focus-within .avatar-edit-btn {
  opacity: 1;
  transform: translateY(0);
}

.avatar-edit-btn {
  position: absolute;
  right: 6px;
  bottom: 6px;
  width: 34px;
  height: 34px;
  border-radius: 50%;
  border: 0;
  background: #17a2b8;
  color: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 10px rgba(0, 0, 0, 0.25);
  opacity: 0;
  transform: translateY(4px);
  transition: opacity 0.2s ease, transform 0.2s ease, background-color 0.2s ease;
}

.avatar-edit-btn:hover:not(:disabled) {
  background: #138496;
}

.avatar-edit-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.impersonation-banner .btn-outline-dark:hover {
  background-color: #856404;
  color: #fff;
}

/* Enhance profile name when impersonating */
.profile-name.impersonating {
  color: #856404;
}

.profile-name.impersonating::after {
  content: " (Impersonating)";
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-regular);
  color: #6c757d;
  font-style: italic;
}

/* Add visual indicator to edit button when impersonating */
.btn-primary.impersonating {
  background-color: #ffc107;
  border-color: #ffc107;
  color: #212529;
}

.btn-primary.impersonating:hover {
  background-color: #e0a800;
  border-color: #d39e00;
  color: #212529;
}

.profile-page {
  margin: -1.5rem -1.5rem 0 -1.5rem;
}

.profile-hero-section {
  position: relative;
  padding: 2rem 0;
  margin-bottom: 0;
}

.profile-hero-background {
  border-radius: 10px 10px 0 0;
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 250px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  background-image: url('/public/bg-header.png');
  background-size: cover;
  background-position: center;
}

.profile-hero-content {
  position: relative;
  z-index: 2;
  padding: 2rem 1.5rem;
  margin-top: 150px;
}

.profile-picture {
  width: 120px;
  height: 120px;
  border-radius: 50%;
  border: 4px solid white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.profile-name {
  color: #2c3e50;
  font-size: var(--font-size-2xl);
  font-weight: var(--font-weight-semibold);
  margin-bottom: 0.5rem;
}

.profile-meta {
  display: flex;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.meta-item {
  color: #6c757d;
  font-size: var(--font-size-sm);
  display: flex;
  align-items: center;
}

.overview-item {
  display: flex;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f1f3f4;
}

.overview-item:last-child {
  border-bottom: none;
}

.overview-item .label {
  margin-left: 0.5rem;
  color: #6c757d;
}

.overview-item .value {
  margin-left: auto;
  font-weight: var(--font-weight-semibold);
  background: #e3f2fd;
  color: #1976d2;
  padding: 0.25rem 0.5rem;
  border-radius: 12px;
  font-size: var(--font-size-sm);
}

.working-group-item {
  padding: 0.5rem 0.75rem;
  color: #fff;
  display: flex;
  align-items: center;
  background: linear-gradient(135deg, #009CAD 0%, #00BCCF 100%);
  margin: 0.25rem 0;
  border-radius: 6px;
  border-left: 3px solid #009CAD;
}

.fellow-item {
  padding: 0.5rem 0;
}

.fellow-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
}

.fellow-name {
  font-weight: var(--font-weight-semibold);
  font-size: var(--font-size-sm);
}

.fellow-title {
  font-size: var(--font-size-sm);
  color: #6c757d;
}

.activity-timeline-container {
  max-height: 300px;
  overflow-y: auto;
  padding: 1rem;
}

.scrollable-timeline {
  position: relative;
}

.timeline-item {
  position: relative;
  padding-left: 2rem;
  margin-bottom: 1.5rem;
}

.timeline-item:last-child {
  margin-bottom: 0;
}

.timeline-marker {
  position: absolute;
  left: 0;
  top: 0.5rem;
  width: 12px;
  height: 12px;
  background: #28a745;
  border-radius: 50%;
  border: 3px solid #e9ecef;
  z-index: 2;
}

.timeline-marker::before {
  content: '';
  position: absolute;
  left: 5px;
  top: 12px;
  width: 2px;
  height: calc(100% + 1.5rem);
  background: #e9ecef;
  z-index: -1;
}

.timeline-item:last-child .timeline-marker::before {
  display: none;
}

.activity-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  object-fit: cover;
}

.activity-text {
  margin-bottom: 0.5rem;
  color: #495057;
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
}

.card {
  border: 1px solid #e9ecef;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
  margin-bottom: 1rem;
}

.card-header {
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  padding: 0.75rem 1rem;
}

.card-header h6 {
  color: #495057;
  font-weight: var(--font-weight-regular);
  letter-spacing: 0.5px;
  font-size: var(--font-size-base);
}

.expandable-sections .card-header:hover {
  background-color: #e9ecef;
}

.table th {
  font-weight: var(--font-weight-semibold);
  padding: 0.5rem;
  border: none;
}

.table td {
  padding: 0.5rem;
  border: none;
  color: #495057;
}

.table-sm th,
.table-sm td {
  padding: 0.3rem 0.5rem;
}

.publication-authors-column {
  word-break: break-word;
}

/* Same label-then-value layout for every publication field (journal, date,
   keywords, authors), instead of dates sitting inline in a table while
   authors stacked underneath their label. */
.publication-meta {
  margin: 0;
}

.publication-meta dt {
  font-weight: var(--font-weight-bold);
}

.publication-meta dd {
  margin: 0 0 0.5rem;
}

.publication-meta dd:last-child {
  margin-bottom: 0;
}

.toast-container .toast {
  min-width: 300px;
}

.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Scrollbar Styling */
.activity-timeline-container::-webkit-scrollbar {
  width: 6px;
}

.activity-timeline-container::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 10px;
}

.activity-timeline-container::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 10px;
}

.activity-timeline-container::-webkit-scrollbar-thumb:hover {
  background: #a8a8a8;
}

.activity-timeline-container {
  scrollbar-width: thin;
  scrollbar-color: #c1c1c1 #f1f1f1;
}

@media (max-width: 768px) {
  .profile-meta {
    flex-direction: column;
    gap: 0.5rem;
  }

  .profile-hero-content {
    padding: 1rem;
    margin-top: 50px;
  }

  .profile-name {
    font-size: var(--font-size-xl);
  }
}

.fellow-item:hover {
  background-color: #f8f9fa;
  border-radius: 8px;
  transition: background-color 0.2s ease;
}

.fellow-avatar {
  object-fit: cover;
}

.card-header {
  background-color: #f7f9fc;
  border-bottom: 1px solid #dee2e6;
}

.text-muted small {
  display: block;
  margin-top: 4px;
}
</style>
